import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Form, Button, Spinner, Alert } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { userActions } from "../../actions/user.actions";
import Utils from "../../utils";
import { FaPerson } from "react-icons/fa6";
import Title from "../common/Title";
import SkeletonForm from "../common/SkeletonForm";

function UserForm(props) {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const [userId, setUserId] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuperUser, setIsSuperUser] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [clientId, setClientId] = useState("");
  const [clientGuid, setClientGuid] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const userReducer = useSelector((state) => state.user);

  useEffect(() => {
    let queryParams = Utils.getQueryStringParams(location);
    const userIdToLoad = props.presetUserId ?? queryParams["userId"];
    if (userIdToLoad) {
      setUserId(userIdToLoad);
      // Pass client context to getById for proper authorization
      const requestParams = {
        userId: userIdToLoad,
        clientId: props.user.clientId,
        clientGuid: props.user.clientGuid,
      };
      dispatch(userActions.getById(requestParams));
    } else {
      setUserId("");
    }
    // eslint-disable-next-line
  }, [location, props.presetUserId]);

  useEffect(() => {
    if (userId && userReducer.userById) {
      const user = userReducer.userById;
      // Backend returns 'active' not 'isActive'
      const activeValue = user.active !== undefined ? user.active : user.isActive;
      setFirstName(user.firstName);
      setLastName(user.lastName);
      setIsAdmin(user.isAdmin);
      setIsSuperUser(user.isSuperUser);
      const activeStatus = activeValue === true;
      setIsActive(activeStatus);
      setClientId(user.clientId);
      setClientGuid(user.clientGuid);
    }
    // eslint-disable-next-line
  }, [userReducer]);

  useEffect(() => {
    if (userReducer.updateSuccess) {
      props.onSaveSuccess ? props.onSaveSuccess() : navigate("/user");
    }
    // eslint-disable-next-line
  }, [userReducer.updateSuccess]);

  const handleSaveUser = () => {
    setSubmitted(true);

    if (firstName && lastName) {
      const userData = {
        firstName,
        lastName,
        isAdmin,
        active: isActive, // Backend expects 'active' not 'isActive'
        // Use the user's stored client info, or fall back to current user's client info
        clientId: clientId || props.user.clientId,
        clientGuid: clientGuid || props.user.clientGuid,
      };

      if (userId) {
        dispatch(userActions.update({ ...userData, userId }));
      }
    }
  };

  return (
    <>
      {!props.hideTitle && <Title text={"Update User"} icon={<FaPerson className="mb-1" />} />}

      {userReducer.loading && <SkeletonForm fields={4} />}

      {!userReducer.loading && (
        <>
          {userReducer.error && (
            <Alert variant="danger">
              An error has occurred. Please try again later.
            </Alert>
          )}

          <Form.Group controlId="formFirstName" className="mb-3">
            <Form.Label>First Name</Form.Label>
            <Form.Control
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className={submitted && !firstName ? "is-invalid" : ""}
            />
            <Form.Control.Feedback type="invalid">
              <small>Please provide a first name.</small>
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group controlId="formLastName" className="mb-3">
            <Form.Label>Last Name</Form.Label>
            <Form.Control
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className={submitted && !lastName ? "is-invalid" : ""}
            />
            <Form.Control.Feedback type="invalid">
              <small>Please provide a last name.</small>
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group controlId="formIsAdmin" className="mb-3">
            <Form.Check
              type="checkbox"
              label="Is Admin"
              checked={isAdmin}
              onChange={(e) => setIsAdmin(e.target.checked)}
            />
          </Form.Group>

          <Form.Group controlId="formIsActive" className="mb-3">
            <Form.Check
              type="checkbox"
              label="Is Active"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
          </Form.Group>

          <Button
            variant="outline-secondary"
            type="button"
            className="me-3"
            onClick={() => (props.onCancel ? props.onCancel() : navigate(-1))}
          >
            Cancel
          </Button>

          <Button
            variant="primary"
            type="button"
            onClick={handleSaveUser}
            disabled={userReducer.loading}
          >
            {!userReducer.loading ? <>Update User</> : <Spinner size="sm" />}
          </Button>
        </>
      )}
    </>
  );
}

export default UserForm;
